import api from './api';
import { cacheService } from './cacheService';
import {
  ClinicSettings,
  UpdateClinicSettingsRequest,
  UpdateBusinessHoursRequest
} from '../types/userManagement';

export const clinicSettingsService = {
  async getSettings(): Promise<ClinicSettings> {
    const cacheKey = 'clinic_settings';
    const cachedData = cacheService.get(cacheKey);
    
    if (cachedData) {
      return cachedData;
    }
    
    const response = await api.get('/clinic-settings');
    const data = response.data.data ? response.data.data : response.data;
    
    // Cache for 10 minutes
    cacheService.set(cacheKey, data, 10 * 60 * 1000);
    
    return data;
  },

  async updateSettings(settings: UpdateClinicSettingsRequest): Promise<ClinicSettings> {
    const response = await api.put('/clinic-settings', settings);
    const responseData = response.data.data ? response.data.data : response.data;
    const data = responseData.settings ? responseData.settings : responseData;
    
    // Update cache
    cacheService.set('clinic_settings', data);
    cacheService.delete('public_clinic_settings');
    
    return data;
  },

  async uploadLogo(logoFile: File): Promise<{ logo_url: string; logo_path: string }> {
    const formData = new FormData();
    formData.append('logo', logoFile);
    
    const response = await api.post('/clinic-settings/upload-logo', formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
    const data = response.data.data ? response.data.data : response.data;
    
    // Invalidate cache
    cacheService.delete('clinic_settings');
    cacheService.delete('public_clinic_settings');
    
    return data;
  },

  async deleteLogo(): Promise<{ success: boolean; message: string }> {
    const response = await api.delete('/clinic-settings/logo');
    const data = response.data.data ? response.data.data : response.data;
    
    // Invalidate cache
    cacheService.delete('clinic_settings');
    cacheService.delete('public_clinic_settings');
    
    return data;
  },

  async getBusinessHours(): Promise<ClinicSettings['formatted_business_hours']> {
    const cacheKey = 'clinic_business_hours';
    const cachedData = cacheService.get(cacheKey);
    
    if (cachedData) {
      return cachedData;
    }
    
    const response = await api.get('/clinic-settings/business-hours');
    const data = response.data;
    
    // Cache for 15 minutes
    cacheService.set(cacheKey, data, 15 * 60 * 1000);
    
    return data;
  },

  async updateBusinessHours(businessHours: UpdateBusinessHoursRequest): Promise<ClinicSettings['formatted_business_hours']> {
    const response = await api.put('/clinic-settings/business-hours', businessHours);
    const responseData = response.data.data ? response.data.data : response.data;
    const data = responseData.business_hours ? responseData.business_hours : responseData;
    
    // Update cache
    cacheService.set('clinic_business_hours', data);
    
    return data;
  },

  async getPublicSettings(): Promise<Partial<ClinicSettings>> {
    const cacheKey = 'public_clinic_settings';
    const cachedData = cacheService.get(cacheKey);
    
    if (cachedData) {
      return cachedData;
    }
    
    const response = await api.get('/public/clinic-settings');
    const data = response.data.data ? response.data.data : response.data;
    
    // Cache for 30 minutes (public settings change less frequently)
    cacheService.set(cacheKey, data, 30 * 60 * 1000);
    
    return data;
  }
};